import { createFileRoute } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C } from '../components/docs/prose'

export const Route = createFileRoute('/docs/performance' as any)({ component: Performance })

const SECTIONS = [
  { id: 'twr', title: 'What time-weighted return measures' },
  { id: 'xirr', title: 'What XIRR measures' },
  { id: 'method', title: 'Why every number carries a method badge' },
  { id: 'snapshots', title: 'Snapshot accounts' },
  { id: 'benchmarks', title: 'How benchmarks are built' },
]

function Performance() {
  return (
    <DocPage
      path="/docs/performance"
      title="Performance"
      lede={
        <>
          &ldquo;How am I doing?&rdquo; is two different questions &mdash; how
          the portfolio performed, and how your dollars did &mdash; and they
          have two different answers. FolioWorth computes both, labels each
          with the method that produced it, and refuses to print a number the
          data cannot support.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="twr" title="What time-weighted return measures">
        <P>
          Time-weighted return (TWR) chains each day&apos;s growth together
          with the effect of your deposits and withdrawals removed. It answers:
          how did the <em>portfolio</em> perform, regardless of when money
          arrived? A big deposit the week before a rally neither flatters nor
          punishes it.
        </P>
        <P>
          That flow-neutrality is what makes TWR the only figure you can fairly
          lay against a benchmark. An index fund never times your paycheque;
          comparing it to a number that does would be comparing your saving
          habits to the market, not your holdings.
        </P>
      </Section>

      <Section id="xirr" title="What XIRR measures">
        <P>
          XIRR is the money-weighted answer: the single annual rate at which
          every dollar you put in would have had to grow to end up where you
          are. It <em>does</em> care when money arrived, so it measures the
          saver as well as the portfolio.
        </P>
        <P>
          The two can honestly disagree. Drip money in through a downturn and
          your XIRR can beat the portfolio&apos;s TWR &mdash; you bought most
          of your units cheap. Dump a windfall in at a peak and the reverse.
          Neither figure is wrong; they answer different questions, which is
          why the page shows both instead of blending them.
        </P>
      </Section>

      <Section id="method" title="Why every number carries a method badge">
        <P>
          A return figure whose provenance is invisible cannot be checked
          against a statement. So every number on the performance page carries
          a badge naming the method that produced it, and the method degrades
          honestly as the data thins:
        </P>
        <DefList
          items={[
            [
              'Time-weighted',
              'Every holding could be priced on enough of the window’s days to chain a daily return. The full-strength figure, benchmark-comparable.',
            ],
            [
              'Value return',
              'Price history has gaps (the page shows the coverage percentage), so the figure is the simple change in value over the window. Deposit timing can distort it; compare against benchmarks with care.',
            ],
            [
              'Not applicable',
              'The window’s data cannot support any honest figure — no priced holdings, or account history the ledger cannot price. Rather than guess, no number is shown.',
            ],
          ]}
        />
        <P>
          The downgrade is per scope: your portfolio can be time-weighted while
          one account inside it, with a thin price history, reads{' '}
          <C>value_return</C> in the comparison table.
        </P>
      </Section>

      <Section id="snapshots" title="Snapshot accounts">
        <P>
          An account started from a holdings snapshot &mdash; &ldquo;here is
          what it held on this date&rdquo; instead of a full trade history
          &mdash; can show performance <em>from the snapshot date forward</em>,
          because from that date the positions and their prices are known.
        </P>
        <P>
          What it cannot show is anything earlier. History before the snapshot
          was never recorded, so a window reaching back past it comes up{' '}
          <C>not_applicable</C> rather than pretending the account sprang into
          existence with a return of zero. And because a snapshot records
          prices on one day, not what you originally paid, its cost base is a
          placeholder: when you graduate the account to transaction tracking,
          review the cost of each position before relying on it at tax time.
        </P>
      </Section>

      <Section id="benchmarks" title="How benchmarks are built">
        <P>
          Benchmark curves are chained from daily closing prices &mdash; each
          day&apos;s close over the previous close, compounded across the
          window. That makes them directly overlayable on your portfolio&apos;s
          flow-neutral curve: both are &ldquo;growth of a dollar left
          alone.&rdquo;
        </P>
        <P>
          Close-to-close chaining measures price only. Distributions are not
          reinvested, so a benchmark&apos;s curve modestly understates its
          total return &mdash; by roughly its yield. Your own figure includes
          dividends your accounts actually received, so if anything the
          comparison is tilted slightly in the benchmark&apos;s disfavour, not
          yours. Honest enough to act on; not a substitute for a fund&apos;s
          published total-return figures.
        </P>
      </Section>
    </DocPage>
  )
}
